import { General } from "../../../siteManagement/general.js";
import { Sheet, SheetEventType } from "../../../siteManagement/sheet.js";
import { StringKeyDictionary } from "../../commonInterfaces.js";
import { KendoTree } from "../../core/components/kendoTree.js";
import { SkillEditBoxProps } from "../../core/components/skills/skillEditBox.js";
import { ToolBarStandardButton, ToolBarStandardButtonType } from "../../core/components/toolBar.js";
import { Config } from "../../core/config/config.js";
import { DialogHelper } from "../../core/util/dialogHelper.js";
import { Helper } from "../../core/util/helper.js";
import { HTTPService, JsonResultClass } from "../../core/util/httpManager.js";
import { NotificationHelper } from "../../core/util/notificationHelper.js";
import { openEditSkillCategory } from "./editSkillTreeCategory.js";
import { Skill, SkillCategoryVm, SkillFilter, TreeNodeVm } from "./skillsInterfaces.js";
import React from "react";

export interface SkillTreeAppProps extends React.PropsWithChildren {
    skillSettings?: SkillSettings;
    onChange?: (dataItem, skills) => void;
    skillFilter: SkillFilter;
    skillEditBoxProps?: SkillEditBoxProps;
    onDataLoaded?: (data: TreeNodeVm[]) => void;
    loadOnDemand?: boolean;
}

interface SkillTreeAppState {
    treeData: TreeNodeVm[];
    selectedNode?: TreeNodeVm;
    useTargetValues?: boolean;
    usePriority?: boolean;
    columnCount?: number;
}

export interface SkillSettings {
    useOgrNr?: boolean;
    useTargetValues?: boolean;
    usePriority?: boolean;

}

export class SkillTreeAppGeneralSettings extends React.Component<any, any>{
    constructor(props) {
        super(props);
        this.state = {
        }
    }

    render() {
        let useOgrNrs = this.props.entity.SkillsSettings.UseMainGroupSkills;
        let usePriority = this.props.entity.SkillsSettings.UsePrioritySkills;
        let useTargetValues = this.props.entity.SkillsSettings.UseTargetValueSkills;
        let skillSettings: SkillSettings = { useOgrNr: useOgrNrs, usePriority: usePriority, useTargetValues: useTargetValues };
        let filter: SkillFilter = { LoadSkills: false, FilterByOgrNr: false, WithDateSkills: true };
        return <SkillTreeApp skillSettings={skillSettings} skillFilter={filter} ></SkillTreeApp>
    }
}

export class SkillTreeApp extends React.Component<SkillTreeAppProps, SkillTreeAppState>{
    private treeRef: KendoTree;
    private translations: StringKeyDictionary<string> = {};
    private sheet: Sheet;

    constructor(props) {
        super(props);
        this.state = {
            treeData: null
        }
    }

    componentDidMount() {
        this.sheet = General.Sheet.Manager.getTopMostSheet();
        this.loadTranslations();
        this.loadData();
        this.setTargetAndPriorityState();
    }

    private setTargetAndPriorityState = () => {
        let useTargetValues = this.props.skillSettings.useTargetValues;
        let usePriority = this.props.skillSettings.usePriority;
        if (Helper.hasValue(this.props.skillEditBoxProps)) {
            useTargetValues = useTargetValues && this.props.skillEditBoxProps.useTargetValues;
            usePriority = usePriority && this.props.skillEditBoxProps.usePriority;
        }

        let columnCount = this.getColumnCount(useTargetValues, usePriority);
        this.setState({ useTargetValues: useTargetValues, usePriority: usePriority, columnCount: columnCount });
    }

    private getColumnCount = (useTargetValues: boolean, usePriority: boolean) => {
        let columnCount = 2;
        columnCount = useTargetValues ? columnCount + 1 : columnCount;
        columnCount = usePriority ? columnCount + 1 : columnCount;
        return columnCount;
    }

    private async loadData() {
        let result = await HTTPService.get<JsonResultClass<TreeNodeVm[]>>("EditSkill/GetSkillsTreeData", this.props.skillFilter);
        if (result.Code == 0) {
            this.setState({ treeData: result.Data }, this.onDataLoaded);
        }
        else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    private onDataLoaded = () => {
        if (Helper.hasValue(this.props.onDataLoaded)) {
            this.props.onDataLoaded(this.state.treeData);
        }
    }

    private async loadTranslations() {
        let result = await Helper.getTranslations(["WebSMS.GeneralSettings.UebersetzungenBearbeiten", "Skills.NoSkillsExist", "Skills.NoCategories",
            "Skills.Category", "Skills.Skill", "Skills.SkillsFilled", "Field.Einsatz.soll", "Einsatz.Actual",
            "Regionen.Projekt.Beschreibung", "MonitorBaum.AlleZuklappen", "MonitorBaum.AlleAufklappen", "WebSMS.Skills.Folder", "Field.Commdocuments.documentno",
            "WebSMS.GeneralSettings.Calender.Date", "WebSMS.Skills.Skill"]);
        if (result.Code == 0) {
            this.translations = result.Data;
        }
        else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    private getTemplate(dataItem) {
        let treeNodeItem: TreeNodeVm = dataItem.item;

        let typIcon = "";
        let text = "";
        let values = "";

        if (treeNodeItem.IsSkill) {
            let skillItem: Skill = treeNodeItem.Item;
            typIcon = `<i class='fal fa-user-graduate' title='${this.translations["WebSMS.Skills.Skill"]}' />`;
            text = skillItem.Rtext;
            if (skillItem.SkillType == 13)
                values = this.getValueElement(skillItem);
        }
        else {
            let categoryItem: SkillCategoryVm = treeNodeItem.Item;

            text = categoryItem.FText;
            typIcon = `<i class='fal fa-folder' title='${this.translations["WebSMS.Skills.Folder"]}' />`;
            switch (categoryItem.Type) {
                case -1:
                    typIcon = `<i class='fal fa-folder' title='${this.translations["WebSMS.Skills.Folder"]}' />`;
                    break;
                case 13:
                    typIcon = `<i class='fal fa-sort-numeric-up-alt' title='${this.translations["Field.Commdocuments.documentno"]}' />`;
                    break;
                case 19:
                    typIcon = `<i class='fal fa-calendar-alt' title='${this.translations["WebSMS.GeneralSettings.Calender.Date"]}' />`;
            }
        }

        return `<div style='display: flex;'>
                    <div>${typIcon}</div>
                    <label style='margin-left: 5px; display: inline-block; margin-right: 5px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; width: 200px;' title='${text}'>
                        ${text}
                    </label> 
                    <div>${values}</div>
                </div>`;
    }

    private getValueElement = (skillItem: Skill) => {
        let value = Helper.hasValue(skillItem.Wert) ? skillItem.Wert.toString() : "0";
        if (this.state.useTargetValues) {
            value = value.concat("/", Helper.hasValue(skillItem.SollWert) ? skillItem.SollWert.toString() : "0");
        }
        return `<span class='skill-tree-item__value'>${value}</span>`;
    }

    private onSelectionChange = (dataItem: TreeNodeVm) => {
        if (Helper.hasValue(dataItem)) {
            if (this.props.loadOnDemand)
                this.loadDataItems(dataItem);
            this.setState({ selectedNode: dataItem }, () => {
                this.sheet.trigger(SheetEventType.Custom, { cmd: "skillCategorySelectionChange", id: dataItem.Id, type: dataItem.Item.Type })
            });
        }
    }

    private loadAllDataItems = () => {
        let roots = this.treeRef.getEntries();
        for (let root of roots) {
            this.loadDataItems(root);
        }
    }

    private loadDataItems = (dataItem) => {
        if (!Helper.hasValue(dataItem) || dataItem.loaded())
            return;
        dataItem.load();
        if (Helper.hasValue(dataItem.Children) && dataItem.Children.length > 0) {
            for (let subItem of dataItem.Children) {
                if (!subItem.loaded())
                    this.loadDataItems(subItem);
            }
        }
    }

    private addNewCategory = async () => {
        let showOgrNrs = this.props.skillSettings.useOgrNr;
        let editResult = await openEditSkillCategory(null, showOgrNrs);
        let parentId = this.state.selectedNode != null ? this.state.selectedNode.Item.Id : 0;
        editResult.SkillCategory.ParentId = parentId;
        let result = await HTTPService.post<JsonResultClass<TreeNodeVm>>("EditSkill/SaveSkillCategory", { skillCategory: editResult });
        if (result.Code == 0) {
            this.treeRef.addNode(result.Data, result.Data.Item.ParentId);
            this.setState({}, () => this.treeRef.selectNode(result.Data.Id));
        } else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    private edit = async (dataItem: TreeNodeVm) => {
        let showOgrNrs = dataItem.Item.Type != -1 && this.props.skillSettings.useOgrNr;
        let editResult = await openEditSkillCategory(dataItem.Item.Id, showOgrNrs);
        let result = await HTTPService.post<JsonResultClass<TreeNodeVm>>("EditSkill/SaveSkillCategory", { skillCategory: editResult });
        if (result.Code == 0) {
            result.Data.Children = dataItem.Children;
            this.replaceNode(result.Data, result.Data.Id);
            this.setState({}, () => this.treeRef.selectNode(result.Data.Id));
        } else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    private replaceNode(data: TreeNodeVm, id) {
        let node = this.treeRef.getNodeById(id);
        if (Helper.hasValue(node)) {
            this.treeRef.setNodeValue(node, "IsSkill", data.IsSkill);
            this.treeRef.setNodeValue(node, "Item", data.Item);
            this.treeRef.setNodeValue(node, "Children", data.Children);
        }
    }

    private deleteNewCategory = async (dataItem: TreeNodeVm) => {
        let isOk = await DialogHelper.confirm(Config.translations.deleteMessage);
        if (isOk) {
            let result = await HTTPService.post<JsonResultClass>('EditSkill/DeleteSkillCategory?fId=' + encodeURIComponent(dataItem.Id))
            if (result.Code == 0) {
                this.treeRef.removeNode(dataItem.Id);
            }
            else {
                NotificationHelper.showErrorMessage(result.Message);
            }
        }
    }

    private afterDataSourceInit = () => {
    }

    private handleClickStandardButton = (type: ToolBarStandardButtonType) => {
        switch (type) {
            case ToolBarStandardButtonType.NEW:
                this.addNewCategory();
                break;
            case ToolBarStandardButtonType.EDIT:
                if (Helper.hasValue(this.state.selectedNode))
                    this.edit(this.state.selectedNode);
                break;

            case ToolBarStandardButtonType.DELETE:
                if (Helper.hasValue(this.state.selectedNode))
                    this.deleteNewCategory(this.state.selectedNode);
                break;
        }
    }

    render() {
        if (!Helper.hasValue(this.state.treeData))
            return null;

        let standardButtons: Array<ToolBarStandardButton> = [
            { type: ToolBarStandardButtonType.NEW, caption: Config.translations.newLabel, enabled: true },
            { type: ToolBarStandardButtonType.EDIT, caption: Config.translations.editLabel, enabled: true },
            { type: ToolBarStandardButtonType.DELETE, caption: Config.translations.deleteLabel, enabled: this.state?.selectedNode && this.state.selectedNode.Item.ChildCount <= 0 }
        ];

        return (<KendoTree
            treeData={this.state.treeData}
            template={(dataItem) => this.getTemplate(dataItem)}
            onSelectionChange={this.onSelectionChange}
            callAfterDataSourceInit={this.afterDataSourceInit}
            ref={(ref) => { this.treeRef = ref; }}
            loadOnDemand={this.props.loadOnDemand}
            showExpandAndCollapseIcons={true}
            standardButtons={standardButtons}
            onClickStandardButton={this.handleClickStandardButton} />);
    }
}